import * as React from 'react';

import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { Dialog, DialogContent } from 'src/registry/ui/Dialog';

type SidePanelProps = {
    name: string;
    children: React.ReactNode;
    onClose?: () => void;
    variant?: 'sheet' | 'leftSheet' | 'leftSheetStatic' | 'default';
};

/**
 * `SidePanel` is a responsive component that displays a side panel on desktop screens and a dialog on smaller screens.
 *
 * @component
 * @param {object} props The properties of the component.
 * @param {string} props.name The unique name of the side panel. This is used to identify the side panel when opening and closing it.
 * @param {React.ReactNode} props.children The content of the side panel.
 * @param {boolean} props.isOpen A boolean indicating whether the side panel is open.
 *
 * @example
 * // To use the `SidePanel` component, pass the `name`, `children`, and `isOpen` props:
 * <SidePanel name="mySidePanel" isOpen={isOpen}>
 *     <p>This is the content of the side panel.</p>
 * </SidePanel>
 *
 * @returns {React.Element} The `SidePanel` component.
 */
const SidePanel = (props: SidePanelProps) => {
    const { name, children, onClose, variant = 'sheet' } = props;
    const { sidePanelState, openPanel, closePanel } = useSidePanel();

    const handleOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            closePanel(name);
            if (onClose) onClose();
        } else {
            openPanel(name);
        }
    };

    React.useEffect(() => {
        return () => {
            closePanel(name);
        };
    }, []);

    return (
        <Dialog modal={false} open={sidePanelState[name]} onOpenChange={handleOpenChange}>
            <DialogContent
                onInteractOutside={(e) => {
                    e.preventDefault();
                }}
                onOpenAutoFocus={(e) => {
                    e.preventDefault();
                }}
                variant={variant}
            >
                {children}
            </DialogContent>
        </Dialog>
    );
};

export default SidePanel;
